import * as React from 'react';
import classNames from "classnames";
import SidebarHideOnNavigation from "./components/SidebarHideOnNavigation";
import SidebarMenu from "./components/SidebarMenu";
import SidebarGroup from "./components/SidebarGroup";
import Icon, { IconType } from "../Icon";
import style from './Sidebar.scss';

const { ENVIRONMENT } = window.ENV;
export const sidebarLogo = ENVIRONMENT === 'production'
  ? require('../../img/logo-prod.svg')
  : ENVIRONMENT === 'stage'
    ? require('../../img/logo-stage.svg')
    : require('../../img/logo-local.svg');

const NARROW_BREAKPOINT = 1200;

export type MenuGroup<S extends {} = {}> = {
  title: string,
  items: Array<MenuItem<S>>,
}

export type MenuItem<S extends {} = {}> = {
  url: string | ((state: S) => string),
  title: string,
  icon?: IconType,
  exact?: boolean,
  tag?: () => React.ReactNode,
  isActive?: (state: S) => boolean,
}

export type Props<S extends {} = {}> = {
  menu: MenuGroup<S>[],
  renderFooter?: () => React.ReactNode,
  getContext?: () => S,
};

const Sidebar: React.FC<Props> = (props) => {
  const { menu, renderFooter, getContext } = props;
  const [hovered, setHovered] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState<boolean>(null);

  React.useEffect(() => {
    const el = document.documentElement;
    el.classList[collapsed ? 'add' : 'remove']('sidebar-collapsed');
    el.classList[collapsed === false ? 'add' : 'remove']('sidebar-expanded');
  }, [collapsed]);

  const context = typeof getContext === 'function' ? getContext() : undefined;

  const onHover = () => setHovered(true);
  const onUnhover = () => setHovered(false);

  const onToggle = () => {
    const isNarrow = window.innerWidth <= NARROW_BREAKPOINT;
    const newCollapsed = isNarrow
      ? collapsed === false
        ? null
        : false
      : collapsed === true
        ? null
        : true
    setCollapsed(newCollapsed);
    setHovered(false);
  }

  const renderGroup = React.useCallback((menuGroup: MenuGroup) => (
    <SidebarGroup
      key={menuGroup.title}
      title={menuGroup.title}
      items={menuGroup.items}
      context={context}
    />
  ), [context]);

  return (
    <>
      <div className={style.placeholder} />
      <div
        className={classNames(style.container, hovered && style.containerHovered)}
        onPointerEnter={onHover}
        onPointerLeave={onUnhover}
      >
        <div className={style.header}>
          <img className={style.logo} src={sidebarLogo} alt="ELSA" />
          <button className={style.closeMenu} onClick={onToggle}>
            <Icon className={style.closeMenuIcon} icon={require('../../icons/MenuToggle.svg')} />
          </button>
        </div>
        <SidebarMenu scrollbarClassname={style.scrollbar}>
          {menu.map(renderGroup)}
        </SidebarMenu>

        {renderFooter && (
          <div className={style.footer}>
            <div className={style.footerContent}>
              {renderFooter()}
            </div>
          </div>
        )}
      </div>
      <SidebarHideOnNavigation onNavigate={onUnhover} />
    </>
  );
}

export default React.memo(Sidebar);
